import React, { useEffect, useState } from "react";
import { IonModal, IonIcon } from "@ionic/react";
import { closeOutline } from "ionicons/icons";

import CustomVimeoPlayer from "../../CustomVimeoPlayer";
import config from "../../../settings/config";
import { getAuthToken } from "../../../services/authStorage";
import "./RecipeVideoOverlay.css";

/**
 * The app's one video popup.
 *
 * Listens to:
 * window.dispatchEvent(new CustomEvent("recipe:playVideo", { detail: { vimeoId } }))
 *
 * Plays through CustomVimeoPlayer, the same player the video detail page uses,
 * so a clip opened from a widget looks like a clip opened anywhere else: the
 * Vimeo poster with a play button centred on it, and the app's own fullscreen
 * control rather than Vimeo's chrome. It replaced a bare Vimeo embed that
 * autoplayed into a black rectangle whenever the SDK was slow or the origin was
 * not whitelisted, which is a poor first impression for a welcome video.
 *
 * Plays as soon as it opens, with sound. A sender can opt out by passing
 * `autoplay: false` in the event detail, which leaves the poster and its play
 * button up instead; nothing does today.
 *
 * Sound rather than the app-wide mute preference: a video someone chose to
 * open should not arrive silent because they muted something in the community
 * feed earlier, the same reasoning the course applies to its lessons.
 */
const EVENT_NAME = "recipe:playVideo";

export default function RecipeVideoOverlay() {
  const [isOpen, setIsOpen] = useState(false);
  const [vimeoId, setVimeoId] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [autoplay, setAutoplay] = useState(true);
  const [aspect, setAspect] = useState(null);
  const [hlsUrl, setHlsUrl] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      const id = detail.vimeoId ?? detail.vimeo_id ?? detail.id;
      if (!id) return;

      setVimeoId(String(id));
      setVideoUrl(detail.videoUrl ?? null);
      setHlsUrl(detail.hlsUrl ?? null);
      setAutoplay(detail.autoplay !== false);

      const w = Number(detail.videoWidth);
      const h = Number(detail.videoHeight);
      setAspect(w > 0 && h > 0 ? w / h : null);

      setIsOpen(true);
    };

    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  // Most senders know only a Vimeo id — the measurements how-to, a challenge
  // card, a profile tab. Without a playable URL and the video's shape this
  // popup fell back to the Vimeo embed inside a fixed 16:9 frame, which is what
  // put black bars around a portrait clip on those screens while the home
  // banner (which passes everything) played it correctly. Resolve the rest here
  // so every caller gets the same player and the same fitted frame.
  // "Guarda su un altro dispositivo": whether this id is a movement video.
  const [offersOtherDevice, setOffersOtherDevice] = useState(false);

  useEffect(() => {
    setOffersOtherDevice(false);
    if (!isOpen || !vimeoId) return;

    const baseUrl = config?.env?.[config?.env?.mode]?.url;
    if (!baseUrl) return;

    let cancelled = false;

    fetch(`${baseUrl}/api/videos/vimeo/${vimeoId}/playback`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${getAuthToken()}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        setOffersOtherDevice(data?.other_device === true);
        const pb = data?.playback;
        // The sender may already have given a stream; only the flag is new then.
        if (!pb || videoUrl || hlsUrl) return;
        setHlsUrl(pb.hls_url ?? null);
        setVideoUrl(pb.mp4_url ?? null);
        const w = Number(pb.width);
        const h = Number(pb.height);
        if (w > 0 && h > 0) setAspect(w / h);
      })
      .catch(() => {
        /* the embed fallback still plays — nothing to recover here */
      });

    return () => {
      cancelled = true;
    };
    // Once per opened video; a stream arriving must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, vimeoId]);

  const close = () => setIsOpen(false);

  // Clearing the id here (not on close) unmounts the player only once the modal
  // has gone, so its teardown never races the dismiss animation.
  const onDidDismiss = () => {
    setIsOpen(false);
    setVimeoId(null);
    setVideoUrl(null);
    setHlsUrl(null);
    setAutoplay(true);
    setAspect(null);
  };

  // The popup takes the video's shape rather than a fixed landscape frame,
  // which is what put black bars either side of a vertical clip.
  //
  // Capping the width by the height keeps a tall video on screen: at 9:16 a
  // full-width popup would be nearly twice the viewport tall. The cap only
  // binds for shapes taller than the screen allows — a 16:9 video asks for far
  // more width than 92vw, so min() leaves it exactly as it is today.
  const modalStyle = aspect
    ? { "--width": `min(92vw, 720px, calc(82vh * ${aspect}))` }
    : undefined;

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      backdropDismiss={true}
      cssClass="recipe-video-popup-modal"
      mode="ios"
      style={modalStyle}
    >
      <div className="recipe-video-popup">
        <button
          type="button"
          className="recipe-video-close"
          onClick={close}
          aria-label="Chiudi il video"
        >
          <IonIcon icon={closeOutline} />
        </button>

        <div
          className="recipe-video-frame"
          // Falls back to the stylesheet's 16:9 when the video's dimensions are
          // unknown (an embed with no playback block), so nothing regresses.
          style={aspect ? { aspectRatio: String(aspect) } : undefined}
        >
          {(videoUrl || hlsUrl || vimeoId) && (
            <CustomVimeoPlayer
              // Native file drives playback; the embed is used only when the
              // backend could not resolve one. Our own controls exist only on
              // the native element, so the embed would hand the member Vimeo's
              // chrome instead.
              //
              // The id is passed even alongside a native URL — the player uses
              // it purely to fetch the Vimeo thumbnail for its poster (its SDK
              // effect returns early whenever videoUrl is set, so this mounts no
              // second player). Without it the popup opens on a black rectangle
              // instead of the still, which is the whole point when it opens by
              // itself and waits to be played.
              video={vimeoId}
              videoUrl={videoUrl}
              hlsUrl={hlsUrl}
              // Fill the 16:9 frame this popup already establishes, instead of
              // the player deriving its own box and leaving bars inside it.
              fillParent={true}
              autoplay={autoplay}
              isWatching={true}
              showFullscreenButton={true}
              fullscreenButtonMode="playing-only"
              controlsVariant={videoUrl || hlsUrl ? "bar" : null}
              otherDevice={
                offersOtherDevice ? { vimeoId: String(vimeoId), context: null } : null
              }
              // Don't inherit a mute set in the feed: start with sound.
              sharedMute={false}
            />
          )}
        </div>
      </div>
    </IonModal>
  );
}
